import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const NewsCard = ({ article, onBookmark, onShare, onViewDetails }) => {
  const getImpactColor = (score) => {
    if (score >= 8) return 'text-error bg-error/10';
    if (score >= 6) return 'text-warning bg-warning/10';
    if (score >= 4) return 'text-accent bg-accent/10';
    return 'text-success bg-success/10';
  };

  const getImpactLabel = (score) => {
    if (score >= 8) return 'High Impact';
    if (score >= 6) return 'Medium Impact';
    if (score >= 4) return 'Low Impact';
    return 'Minimal Impact';
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const articleTime = new Date(timestamp);
    const diffInMinutes = Math.floor((now - articleTime) / (1000 * 60));
    
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 hover:shadow-elevated transition-shadow duration-200">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`px-2 py-1 rounded-full text-xs font-medium ${getImpactColor(article?.impactScore)}`}>
            {getImpactLabel(article?.impactScore)}
          </div>
          <span className="text-xs text-muted-foreground font-medium">
            {article?.impactScore}/10
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onBookmark(article?.id)}
            className="p-1"
          >
            <Icon 
              name={article?.isBookmarked ? "Bookmark" : "BookmarkPlus"} 
              size={16}
              className={article?.isBookmarked ? "text-accent" : "text-muted-foreground"}
            />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onShare(article)}
            className="p-1"
          >
            <Icon name="Share2" size={16} className="text-muted-foreground" />
          </Button>
        </div>
      </div>
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-foreground mb-2 line-clamp-2">
          {article?.headline}
        </h3>
        <p className="text-sm text-muted-foreground line-clamp-3 mb-3">
          {article?.summary}
        </p>
      </div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Icon name="Building2" size={14} className="text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">{article?.source}</span>
          </div>
          <div className="flex items-center gap-2">
            <Icon name="Clock" size={14} className="text-muted-foreground" />
            <span className="text-sm text-muted-foreground">{formatTimeAgo(article?.publishedAt)}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {article?.relevantMarkets?.map((market) => (
            <span
              key={market}
              className={`px-2 py-1 rounded-full text-xs font-medium ${
                market === 'EUA' ?'bg-success/10 text-success' :'bg-primary/10 text-primary'
              }`}
            >
              {market}
            </span>
          ))}
        </div>
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Icon name="TrendingUp" size={14} className="text-muted-foreground" />
            <span className="text-sm text-muted-foreground">
              Sentiment: <span className={`font-medium ${
                article?.sentiment === 'positive' ? 'text-success' :
                article?.sentiment === 'negative' ? 'text-error' : 'text-warning'
              }`}>
                {article?.sentiment?.charAt(0)?.toUpperCase() + article?.sentiment?.slice(1)}
              </span>
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Icon name="BarChart3" size={14} className="text-muted-foreground" />
            <span className="text-sm text-muted-foreground">
              Correlation: {article?.marketCorrelation}%
            </span>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onViewDetails(article)}
          className="flex items-center gap-2"
        >
          <span>View Details</span>
          <Icon name="ExternalLink" size={14} />
        </Button>
      </div>
    </div>
  );
};

export default NewsCard;